import React, { useState, useContext, useEffect } from 'react'
import './homepages.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faClock, faBook, faCompass } from '@fortawesome/pro-regular-svg-icons'
import { useParams } from '@reach/router'
import { Link } from 'gatsby'
import useSingleProgramDB from './shared/useSingleProgramDB'
import { FirebaseContext } from '../Firebase'
import LoadingSpinner from '../../components/loading/LoadingSpinner.js'
import { navigate } from 'gatsby'
import NotFoundSection from '../../templates/404.js'
import { FormattedMessage, useIntl } from 'react-intl'
import DOMPurify from 'dompurify'
import { ImageWithFallback } from './views/display.js'
import { useQueryParam, StringParam } from 'use-query-params'
import { WingsButton } from '@wingspans-ui'

/**
 * Displays a program's home page with course overview, modules, and navigation.
 * @param {Object} props - Component props
 * @param {string} props.category - Program category label
 * @param {string} props.categoryEs - Spanish program category label
 * @param {string} props.title - Program title
 * @param {string} props.titleEs - Spanish program title
 * @param {string} props.description - Program description
 * @param {string} props.descriptionEs - Spanish program description
 * @param {string} props.image - Program banner image URL
 * @param {string} props.whyComplete - Reason to complete the program
 * @param {string} props.whyCompleteEs - Spanish reason to complete
 * @param {string} props.bannerColor - CSS gradient class name for banner
 * @param {string} props.quote - Inspirational quote
 * @param {string} props.quoteEs - Spanish inspirational quote
 * @param {string} props.quoteAuthor - Quote attribution
 * @param {Array} props.objectives - Learning objectives list
 * @param {Array} props.objectivesEs - Spanish learning objectives
 * @param {string} props.time - Program duration
 * @param {string} props.timeEs - Spanish program duration
 * @param {Array} props.programModuleList - List of program modules
 * @param {number} props.selectedModuleIndex - Currently selected module index
 * @param {Function} props.setSelectedModuleIndex - Update selected module index
 * @param {Function} props.handleRestartModule - Handle module restart action
 * @param {boolean} props.isProgramLoading - Loading state
 * @param {number} props.numModulesCompleted - Number of completed modules
 * @param {number} props.numModulesTotal - Total module count
 * @param {boolean} props.isPreview - Whether in preview mode
 * @param {string} props.certificateId - Certificate ID if program completed
 */
export const ProgramHomeDisplay = ({
  category,
  categoryEs,
  title,
  titleEs,
  description,
  descriptionEs,
  image,
  whyComplete,
  whyCompleteEs,
  bannerColor,
  quote,
  quoteEs,
  quoteAuthor,
  objectives,
  objectivesEs,
  time,
  timeEs,
  programModuleList = [],
  selectedModuleIndex,
  setSelectedModuleIndex,
  handleRestartModule,
  isProgramLoading,
  numModulesCompleted,
  numModulesTotal,
  isPreview,
  certificateId,
}) => {
  console.log('certifcate id', certificateId)
  const { locale } = useIntl()
  const [programParam] = useQueryParam('program', StringParam)
  const [previewParam] = useQueryParam('preview', StringParam)

  // Maps banner color prop to CSS gradient class name
  const renderGradientClassName = () => {
    switch (bannerColor) {
      case 'blue':
        return 'blue-gradient'
      case 'red':
        return 'red-gradient'
      case 'orange':
        return 'orange-gradient'
      case 'yellow':
        return 'yellow-gradient'
      case 'green':
        return 'green-gradient'
      case 'purple':
        return 'purple-gradient'
      default:
        return 'blue-gradient'
    }
  }

  // Determines the button text based on module status and preview mode
  const renderButtonText = (moduleStatus) => {
    if (isPreview || !!previewParam) {
      return 'Preview Module'
    }

    if (!!programParam) {
      return <FormattedMessage id="curriculum-go-to-module" defaultMessage="Go To Module" />
    }

    switch (moduleStatus) {
      case 'in_progress':
        return <FormattedMessage id="curriculum-continue" defaultMessage="Continue" />
      case 'completed':
        return <FormattedMessage id="curriculum-restart-module" defaultMessage="Restart" />
      default:
        return <FormattedMessage id="curriculum-start-module" defaultMessage="Get Started" />
    }
  }

  // Determines the banner button text based on module status and preview mode
  const renderBannerButtonText = (moduleStatus) => {
    if (isPreview || !!previewParam) {
      return 'Preview Program'
    }

    if (!!programParam) {
      return <FormattedMessage id="curriculum-go-to-module" defaultMessage="Go To Module" />
    }

    switch (moduleStatus) {
      case 'in_progress':
        return <FormattedMessage id="curriculum-continue" defaultMessage="Continue" />
      case 'completed':
        return <FormattedMessage id="curriculum-restart-module" defaultMessage="Restart" />
      default:
        return <FormattedMessage id="curriculum-start-module" defaultMessage="Get Started" />
    }
  }

  if (isProgramLoading) {
    return <LoadingSpinner />
  }

  // Localized quote based on user's locale
  const localizedQuote = locale === 'es-ES' ? quoteEs : quote
  // Localized program completion description
  const localizedFullDescription = locale === 'es-ES' ? whyCompleteEs : whyComplete
  // Localized program category label
  const localizedCategory = locale === 'es-ES' ? categoryEs : category
  // Localized program title
  const localizedTitle = locale === 'es-ES' ? titleEs : title
  // Localized program description
  const localizedDescription = locale === 'es-ES' ? descriptionEs : description
  // Localized learning objectives/skills
  const localizedSkills = locale === 'es-ES' ? objectivesEs : objectives
  
  // Maps program modules to localized properties based on user locale
  const localizedModules = programModuleList.map((module) => {
    const localizedModule = {
      status: module.status,
      module_id: module.module_id,
      program_id: module.program_id,
    }
    const module_json = module.module_json || {}
    if (locale === 'es-ES') {
      localizedModule.title = module_json.titleEs || module_json.title
      localizedModule.time = module_json.timeEs || module_json.time
      localizedModule.whatLearn = module_json.whatLearnEs || module_json.whatLearn
      localizedModule.directions = module_json.directionsEs || module_json.directions
    } else {
      localizedModule.title = module_json.title
      localizedModule.time = module_json.time
      localizedModule.whatLearn = module_json.whatLearn
      localizedModule.directions = module_json.directions
    }
    return localizedModule
  })

  // Finds the next incomplete module in the program
  const upNextModule = localizedModules[localizedModules?.findIndex((module) => module.status !== 'completed')] || null
  // Currently selected module for display in the content panel
  const selectedModule = localizedModules[selectedModuleIndex || 0] || null

  // Determines if the user has started the program
  const hasProgramStarted = localizedModules.some((module) => module.status === 'in_progress' || module.status === 'completed')

  // Generates the URL for the banner button, routing to first module in preview or next incomplete module
  const getBannerButtonUrl = () => {
    // In preview mode, always point to first module
    const targetModule = isPreview || !!previewParam ? localizedModules[0] : upNextModule
    if (!targetModule) return '#'

    const baseUrl = `${locale === 'es-ES' ? '/es-ES' : ''}/modules/${targetModule.module_id}/interactive?program=${targetModule.program_id}`

    if (isPreview || !!previewParam) {
      return `${baseUrl}&preview=1`
    }

    return baseUrl
  }

  // Generates the URL for individual module action buttons based on module and preview status
  const getModuleButtonUrl = (module) => {
    if (!module) return '#'

    const baseUrl = `${locale === 'es-ES' ? '/es-ES' : ''}/modules/${module.module_id}/interactive?program=${module.program_id}`

    if (isPreview || !!previewParam) {
      return `${baseUrl}&preview=1`
    }

    return baseUrl
  }

  // Constructs a root domain URL, removing any subdomain for certificate access
  function getRootDomainUrl(path) {
    if (typeof window === 'undefined') return path
    const { protocol, pathname, search, hash, host } = window.location
    const hostParts = host.split('.')
    const rootHost = hostParts.length > 2 ? hostParts.slice(-2).join('.') : host
    return `${protocol}//${rootHost}${path}`
  }

  return (
    <div className="curriculum-home program">
      <div className={`home-banner ${renderGradientClassName()}`}>
        <div className="inner">
          <div className="home-container">
            <div className="home-header">
              <label className="home-label">{localizedCategory}</label>
              <h2 className="home-title">{localizedTitle}</h2>
              <p className="home-description">{localizedDescription}</p>
              <ImageWithFallback className="home-image" src={image} />
            </div>

            <div className="home-cta">
              {localizedModules && upNextModule ? (
                <>
                  <div className="home-cta-next-module">
                    <label>
                      {hasProgramStarted ? (
                        <FormattedMessage id="curriculum-up-next" defaultMessage="You Can Do It!" />
                      ) : (
                        <FormattedMessage id="curriculum-get-started" defaultMessage="Get Started" />
                      )}
                    </label>{' '}
                    <h6>
                      {isPreview || !!previewParam
                        ? localizedModules[0]?.title
                        : localizedModules[localizedModules?.findIndex((module) => module.status !== 'completed')]?.title}
                    </h6>
                  </div>
                  <Link to={getBannerButtonUrl()}>
                    <WingsButton variant="primary" size="small">
                      {renderBannerButtonText(
                        isPreview || !!previewParam
                          ? localizedModules[0]?.status
                          : localizedModules[localizedModules?.findIndex((module) => module.status !== 'completed')]?.status
                      )}
                    </WingsButton>
                  </Link>
                </>
              ) : (
                <>
                  <h4>
                    <FormattedMessage id="curriculum-program-completed" defaultMessage="Program Completed" />
                  </h4>
                  <p>
                    <FormattedMessage
                      id="curriculum-congratulations-on-completing-all-modules"
                      defaultMessage="Congratulations on completing all modules!"
                    />
                  </p>
                  {certificateId && (
                    <a href={getRootDomainUrl(`/certificates/${certificateId}`)} target="_blank" rel="noopener noreferrer">
                      <WingsButton variant="primary" size="small">
                        <FormattedMessage id="curriculum-view-certificate" defaultMessage="View Certificate" />
                      </WingsButton>
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="home-container inner">
        <div className="home-content">
          <div className="home-why-complete">
            <h3>
              <FormattedMessage id="curriculum-why-complete-this-program" defaultMessage="Why complete this program" />
            </h3>
            {!!localizedQuote && (
              <>
                <blockquote>"{localizedQuote}"</blockquote>
                {quoteAuthor && <label>-{quoteAuthor}</label>}
              </>
            )}
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedFullDescription) }} />
          </div>
          {localizedSkills?.length > 0 && (
            <div className="home-skills">
              <h6>
                <FormattedMessage id="curriculum-skills-you-will-develop" defaultMessage="Skills you will develop:" />
              </h6>
              <div className="home-skills-list">
                {localizedSkills.map((skill, index) => (
                  <div key={index} className="home-skill">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="home-tasks">
          <div className="home-tasks-header">
            <h3>
              <FormattedMessage id="curriculum-section-overview" defaultMessage="Section overview" />
            </h3>
            {/* <label>
              {" "}
              {numModulesCompleted} / {numModulesTotal} Complete
            </label> */}
          </div>
          <div className="home-tasks-container">
            <aside className="home-tasks-aside">
              {localizedModules &&
                localizedModules.map((module, index) => (
                  <div
                    key={index}
                    className={`home-task ${selectedModuleIndex === index ? 'selected' : ''}`}
                    onClick={() => setSelectedModuleIndex(index)}
                  >
                    {module.status === 'completed' ? (
                      <div className="home-task-number">
                        <FontAwesomeIcon icon={faCheckCircle} color="#63C0D5" size="lg" />
                      </div>
                    ) : (
                      <div className="home-task-number">{index + 1}</div>
                    )}
                    <div>{module.title}</div>
                  </div>
                ))}
            </aside>
            <div className="home-task-content">
              <div className="home-task-content-inner">
                <h5>
                  <FormattedMessage id="curriculum-overview" defaultMessage="Overview" />
                </h5>
                {localizedModules && selectedModule && (
                  <>
                    {' '}
                    {selectedModule?.time && (
                      <span>
                        {' '}
                        <FontAwesomeIcon icon={faClock} color="#63C0D5" />
                        {selectedModule?.time}
                      </span>
                    )}
                    {/* <p>{selectedModule?.description}</p> */}
                    <div className="home-task-overview">
                      <div className="home-task-overview-icon">
                        <FontAwesomeIcon icon={faBook} color="#63C0D5" size="2x" />
                      </div>
                      <div>
                        <h5>
                          <FormattedMessage id="curriculum-what-you-will-learn" defaultMessage="What you will learn" />
                        </h5>
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedModule.whatLearn) }} />
                      </div>
                    </div>
                    <div className="home-task-overview" style={{ marginTop: '1rem' }}>
                      <div className="home-task-overview-icon">
                        <FontAwesomeIcon icon={faCompass} color="#63C0D5" size="2x" />
                      </div>
                      <div>
                        <h5>
                          <FormattedMessage id="curriculum-directions" defaultMessage="Directions" />
                        </h5>
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedModule.directions) }} />
                      </div>
                    </div>
                    {selectedModule?.status === 'completed' ? (
                      isPreview || !!previewParam ? (
                        <Link to={getModuleButtonUrl(selectedModule)} className="program-home-button-link">
                          <WingsButton variant="primary" size="mini" className="program-home-action-button">
                            {renderButtonText(selectedModule?.status)}
                          </WingsButton>
                        </Link>
                      ) : (
                        <a className="program-home-button-link">
                          <WingsButton
                            variant="primary"
                            size="mini"
                            onClick={() => handleRestartModule(selectedModule?.module_id)}
                            className="program-home-action-button"
                          >
                            {renderButtonText(selectedModule?.status)}
                          </WingsButton>
                        </a>
                      )
                    ) : (
                      <Link to={getModuleButtonUrl(selectedModule)} className="program-home-button-link">
                        <WingsButton variant="primary" size="mini" className="program-home-action-button">
                          {renderButtonText(selectedModule?.status)}
                        </WingsButton>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Main component for the program home page. Manages program data loading and module selection.
 * @param {Object} props - Component props
 * @param {boolean} props.isPreview - Whether the program is being previewed
 * @returns {React.ReactNode} The program home page component
 */
export const ProgramHome = ({ isPreview }) => {
  // Context
  const { firebase, user, loading, isUserLoggedOut } = useContext(FirebaseContext)

  // Custom hooks
  const { programId } = useParams()
  const { programData, programModuleList = [], isProgramLoading, certificateId } = useSingleProgramDB(programId, firebase)

  // State
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0)

  // Derived constants
  const {
    category = '',
    category_es,
    title,
    title_es,
    objectives = [],
    objectives_es = [],
    quote,
    quote_es,
    description,
    description_es,
    why_complete,
    why_complete_es,
    image,
    quote_author,
    banner_color,
  } = programData || {}

  // Total number of modules in the program
  const numModulesTotal = programModuleList.length
  // Count of modules that have been completed by the user
  const numModulesCompleted = programModuleList.filter((module) => module.status === 'completed').length

  // Local functions
  /**
   * Handles restarting a module by clearing its progress and navigating to the module page.
   * @param {string} moduleId - The ID of the module to restart
   */
  const handleRestartModule = async (moduleId) => {
    await firebase.restartUserModule(moduleId)
    navigate(
      `/modules/${programModuleList[selectedModuleIndex]?.module_id}/interactive?program=${programModuleList[selectedModuleIndex]?.program_id}`
    )
  }

  // Effects
  // Redirect to login if user is logged out
  useEffect(() => {
    if (isUserLoggedOut) {
      navigate(`/login/?redirectTo=/programs/${programId}`, { replace: true })
    }
  }, [isUserLoggedOut])

  // Update selected module when the program module list changes
  useEffect(() => {
    if (programModuleList.length > 0) {
      const completedModuleIndex = programModuleList.findIndex((module) => module.status !== 'completed')
      completedModuleIndex === -1 ? setSelectedModuleIndex(0) : setSelectedModuleIndex(completedModuleIndex)
    }
  }, [programModuleList])

  // TODO: Display NotFoundSection if programData is null after loading. Currently flickering because of hook issue
  // if (!programData && !isProgramLoading) {
  //   return <NotFoundSection />
  // }

  if (isProgramLoading || !firebase || !programData) {
    return <LoadingSpinner />
  }

  return (
    <ProgramHomeDisplay
      category={category}
      categoryEs={category_es}
      title={title}
      titleEs={title_es}
      description={description}
      descriptionEs={description_es}
      image={image}
      whyComplete={why_complete}
      whyCompleteEs={why_complete_es}
      objectives={objectives}
      objectivesEs={objectives_es}
      programModuleList={programModuleList}
      selectedModuleIndex={selectedModuleIndex}
      setSelectedModuleIndex={setSelectedModuleIndex}
      quote={quote}
      quoteEs={quote_es}
      quoteAuthor={quote_author}
      bannerColor={banner_color}
      handleRestartModule={handleRestartModule}
      isProgramLoading={isProgramLoading}
      numModulesCompleted={numModulesCompleted}
      numModulesTotal={numModulesTotal}
      isPreview={isPreview}
      certificateId={certificateId}
    />
  )
}
